import { memo, useMemo, type CSSProperties } from 'react';
import { TrackMarkers, type ITrackMarkersProps } from './TrackMarkers';
import { REPLAY_LANES, type IDayMarkers, type IPlacedEvent } from './markers';
import type { ReplayMarkerView } from './useMarkerView';
import styles from './ReplayTrack.module.css';

/** One row of markers: its events and where it sits. */
interface IMarkerRow {
  key: string;
  className?: string;
  style?: CSSProperties;
  placed: IPlacedEvent[];
}

type ITrackMarkerRowsProps = Omit<ITrackMarkersProps, 'placed'> & {
  /** The day's markers, placed once by the bar (see dayMarkers). */
  markers: IDayMarkers;
  view: ReplayMarkerView;
};

/**
 * The day's markers, off the curve: one row of every kind between the curve and
 * the hours (badges), or one row per lane under the hours with the peak left on
 * the curve (lanes). Each row clusters and nudges its own markers.
 */
export const TrackMarkerRows = memo(function TrackMarkerRows({ markers, view, ...rowProps }: ITrackMarkerRowsProps) {
  const rows = useMemo(
    (): IMarkerRow[] =>
      view === 'badges'
        ? [{ key: 'badges', className: styles.badgeRow, placed: markers.all }]
        : [
            { key: 'curve', className: styles.curveRow, placed: markers.byLane.curve },
            ...REPLAY_LANES.map((lane, index) => ({
              key: lane,
              className: styles.lane,
              style: { '--lane': index } as CSSProperties,
              placed: markers.byLane[lane],
            })),
          ],
    [markers, view],
  );

  return rows.map(({ key, className, style, placed }) => (
    <div key={key} className={className} style={style} data-testid={`replay-row-${key}`}>
      <TrackMarkers placed={placed} {...rowProps} />
    </div>
  ));
});
